import React, { useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../../utils/cn';

/**
 * Sterowanie zwijaniem karty z zewnątrz (SimpleWISCVSystem trzyma mapę
 * {cardId: boolean} i podaje parę open/onOpenChange do każdej karty).
 */
export interface CollapseControl {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CollapsibleCardProps {
  title: string;
  icon: LucideIcon;
  /** Controlled: stan otwarcia. Gdy pominięty, karta działa w trybie uncontrolled (defaultOpen). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Stan początkowy w trybie uncontrolled (domyślnie true). */
  defaultOpen?: boolean;
  /** Subtelny pulsujący ring na tokenach accent — aktywna karta podczas generowania. */
  highlight?: boolean;
  /** Klasy korpusu karty (np. zmiana paddingu / układu treści). */
  contentClassName?: string;
  /** Klasy tytułu (rozmiar/kolor) — domyślnie text-base text-txt-primary. */
  titleClassName?: string;
  /** Klasy ikony — domyślnie text-accent. */
  iconClassName?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Reużywalna zwijana karta starego UI (Warm paper).
 * Animacja wysokości: Radix Collapsible + keyframes collapsibleSlideDown/Up
 * w index.css (oparte o --radix-collapsible-content-height, ~300 ms,
 * wyłączane przy prefers-reduced-motion).
 */
export const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
  title,
  icon: Icon,
  open,
  onOpenChange,
  defaultOpen = true,
  highlight = false,
  contentClassName,
  titleClassName,
  iconClassName,
  className,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      className={cn(
        // shrink-0: karty żyją w przewijanych kolumnach flex (lg:max-h + overflow-y-auto);
        // bez tego overflow-hidden pozwala flexowi ścisnąć kartę poniżej wysokości treści.
        'glass-pane shrink-0 rounded-2xl text-txt-primary relative overflow-hidden',
        highlight && 'card-streaming-active',
        className
      )}
    >
      <Collapsible.Trigger asChild>
        <button
          type="button"
          aria-label={isOpen ? `Zwiń sekcję: ${title}` : `Rozwiń sekcję: ${title}`}
          className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left cursor-pointer select-none group focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-2xl transition-colors duration-200"
        >
          <span
            className={cn(
              'text-base font-semibold text-txt-primary flex items-center gap-2 min-w-0',
              titleClassName
            )}
          >
            <Icon className={cn('w-5 h-5 text-accent shrink-0', iconClassName)} />
            <span className="truncate">{title}</span>
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'w-4 h-4 text-txt-tertiary shrink-0 transition-transform duration-300 ease-out group-hover:text-txt-secondary motion-reduce:transition-none',
              isOpen && 'rotate-180'
            )}
          />
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content className="collapsible-content">
        <div className={cn('px-5 pb-5', contentClassName)}>{children}</div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};
